import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, Menu, X, LayoutDashboard, LogOut, MessageSquare, Building, Crown, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isLanding = location.pathname === '/';

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'How it Works', href: '#how' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <header className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/70 border-b border-slate-200/60">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center shadow-md shadow-rose-200 group-hover:scale-105 transition-transform">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-bold text-slate-900 tracking-tight">MyBusinessAdPro</span>
            <span className="text-[10px] text-slate-500 font-medium">AI Ads for Local Business</span>
          </div>
        </Link>

        {isLanding && (
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map(l => (
              <a key={l.href} href={l.href} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                {l.label}
              </a>
            ))}
          </nav>
        )}

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-slate-700">
                <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
              </Button>
              <Button variant="ghost" onClick={() => navigate('/reviews')} className="text-slate-700">
                <MessageSquare className="w-4 h-4 mr-2" /> Reviews
              </Button>
              <Button variant="ghost" onClick={() => navigate('/gbp')} className="text-slate-700">
                <Building className="w-4 h-4 mr-2" /> GBP
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white text-sm font-semibold flex items-center justify-center hover:ring-2 hover:ring-orange-200 transition-all">
                    {user.name?.charAt(0).toUpperCase() || 'U'}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-semibold">{user.name}</div>
                    <div className="text-xs text-slate-500 font-normal">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/create')}>
                    <Sparkles className="w-4 h-4 mr-2" /> Create Ad
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/reviews')}>
                    <MessageSquare className="w-4 h-4 mr-2" /> AI Review Replies
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/share-guide')}>
                    <Share2 className="w-4 h-4 mr-2" /> How to post on socials
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/gbp')}>
                    <Building className="w-4 h-4 mr-2" /> Google Business
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings/businesses')}>
                    <Building className="w-4 h-4 mr-2" /> Your businesses
                  </DropdownMenuItem>
                  {user.isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <Crown className="w-4 h-4 mr-2 text-orange-500" /> Founder Admin
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/analytics')}>
                        <Crown className="w-4 h-4 mr-2 text-orange-500" /> Founder Analytics
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }}>
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-slate-900 px-3 py-2">
                Sign in
              </Link>
              <Button onClick={() => navigate('/signup')} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-semibold">
                Start Free
              </Button>
            </>
          )}
        </div>

        <button className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white px-5 py-4 space-y-3 max-h-[80vh] overflow-y-auto">
          {isLanding && navLinks.map(l => (
            <a key={l.href} href={l.href} onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-slate-700 py-1.5">
              {l.label}
            </a>
          ))}
          <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
            {user ? (
              <>
                <Button onClick={() => { navigate('/dashboard'); setMobileOpen(false); }} variant="outline" className="justify-start">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </Button>
                <Button onClick={() => { navigate('/create'); setMobileOpen(false); }} variant="outline" className="justify-start">
                  <Sparkles className="w-4 h-4 mr-2" /> Create ad
                </Button>
                <Button onClick={() => { navigate('/reviews'); setMobileOpen(false); }} variant="outline" className="justify-start">
                  <MessageSquare className="w-4 h-4 mr-2" /> Reviews
                </Button>
                <Button onClick={() => { navigate('/gbp'); setMobileOpen(false); }} variant="outline" className="justify-start">
                  <Building className="w-4 h-4 mr-2" /> Google Business
                </Button>
                {user.isAdmin && (
                  <Button onClick={() => { navigate('/admin'); setMobileOpen(false); }} variant="outline" className="justify-start">
                    <Crown className="w-4 h-4 mr-2 text-orange-500" /> Founder Admin
                  </Button>
                )}
                <Button onClick={() => { logout(); setMobileOpen(false); navigate('/'); }} variant="ghost" className="justify-start">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => { navigate('/login'); setMobileOpen(false); }} variant="outline">Sign in</Button>
                <Button onClick={() => { navigate('/signup'); setMobileOpen(false); }} className="bg-slate-900 text-white">Start Free</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
